import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import request from '../../utils/request';
import { useAuth } from '../../hooks';
import AuthCharacters from './AuthCharacters';
import styles from './index.module.css';

function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserInfo } = useAuth();

  const [mode, setMode] = useState('login');

  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: ''
  });

  const [userInputCode, setUserInputCode] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const logoImg = './images/office/baby.png';
  const companyName = '知微正价';
  // const backgroundImg = './images/office/authbackimage.png';

  const currentEmail = mode === 'login' ? loginForm.email : registerForm.email;
  const currentPassword = mode === 'login' ? loginForm.password : registerForm.password;

  const getDeviceInfo = () => {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      localStorage.setItem('device_id', deviceId);
    }

    const getDeviceType = () => {
      const ua = navigator.userAgent.toLowerCase();

      if (
        /(ipad|tablet|(android(?!.*mobile)))/i.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      ) {
        return 'tablet';
      }

      if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
        if (/android/i.test(ua)) return 'android';
        if (/iphone|ipod|ios/i.test(ua)) return 'ios';
        return 'mobile';
      }

      return 'pc';
    };

    return { deviceId, deviceType: getDeviceType() };
  };

  const fetchCaptcha = async () => {
    try {
      setCaptchaLoading(true);
      const response = await request.get('/api/AppSystemDB/captcha');

      if (response.data.success) {
        setCaptchaId(response.data.captchaId);
        setCaptchaSvg(response.data.captchaSvg);
        setUserInputCode('');
        setCaptchaError(false);
      } else {
        setSubmitError(response.data.message || '验证码加载失败');
      }
    } catch (error) {
      console.error('获取验证码失败:', error);
      setSubmitError('验证码加载失败，请稍后重试');
    } finally {
      setCaptchaLoading(false);
    }
  };

  const refreshCaptcha = () => {
    fetchCaptcha();
    setUserInputCode('');
    setCaptchaError(false);
  };

  const clearErrors = () => {
    setSubmitError('');
    setErrors({});
    setCaptchaError(false);
  };

  useEffect(() => {
    if (location.pathname === '/register') {
      setMode('register');
    } else {
      setMode('login');
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchCaptcha();

    if (location.state?.email) {
      setLoginForm(prev => ({
        ...prev,
        email: location.state.email
      }));
    }
  }, [location.state]);

  const validateLogin = () => {
    const newErrors = {};

    if (!loginForm.email.trim()) {
      newErrors.email = '邮箱不能为空';
    } else if (!/\S+@\S+\.\S+/.test(loginForm.email.trim())) {
      newErrors.email = '邮箱格式不正确';
    }

    if (!loginForm.password) {
      newErrors.password = '请输入密码';
    }

    if (!userInputCode.trim()) {
      newErrors.verificationCode = '请输入验证码';
    }

    if (!captchaId) {
      newErrors.verificationCode = '验证码无效，请刷新后重试';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRegister = () => {
    const newErrors = {};

    if (!registerForm.username.trim()) {
      newErrors.username = '用户名不能为空';
    } else if (!/^[\u4e00-\u9fa5a-zA-Z0-9_]{2,20}$/.test(registerForm.username.trim())) {
      newErrors.username = '用户名支持中文、字母、数字、下划线，长度2-20位';
    }

    if (!registerForm.email.trim()) {
      newErrors.email = '邮箱不能为空';
    } else if (!/\S+@\S+\.\S+/.test(registerForm.email.trim())) {
      newErrors.email = '邮箱格式不正确';
    }

    if (!registerForm.password) {
      newErrors.password = '密码不能为空';
    } else if (registerForm.password.length < 6) {
      newErrors.password = '密码至少6位';
    }

    if (!registerForm.confirmPassword) {
      newErrors.confirmPassword = '请确认密码';
    } else if (registerForm.password !== registerForm.confirmPassword) {
      newErrors.confirmPassword = '两次密码不一致';
    }

    if (!registerForm.inviteCode.trim()) {
      newErrors.inviteCode = '请输入邀请码';
    }

    if (!userInputCode.trim()) {
      newErrors.verificationCode = '请输入验证码';
    }

    if (!captchaId) {
      newErrors.verificationCode = '验证码无效，请刷新后重试';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    clearErrors();

    if (!validateLogin()) return;

    setLoading(true);
    const { deviceId, deviceType } = getDeviceInfo();

    try {
      const response = await request.post('/api/auth/login', {
        email: loginForm.email.trim(),
        password: loginForm.password,
        device_id: deviceId,
        device_type: deviceType,
        captchaId,
        captchaCode: userInputCode.trim().toUpperCase()
      });

      if (response.data.success) {
        localStorage.setItem('device_id', deviceId);
        localStorage.setItem('device_type', deviceType);

        const userData = {
          ...response.data.user,
          loginTime: new Date().toISOString()
        };

        const token = response.data.token;
        const expiresAt = response.data.expiresAt;

        if (!token) {
          throw new Error('服务器未返回Token');
        }

        setUserInfo(userData, token, expiresAt);

        const from = location.state?.from?.pathname || '/home';
        navigate(from, { replace: true });
      } else {
        throw new Error(response.data.message || '登录失败');
      }
    } catch (error) {
      console.error('登录错误:', error);

      let errorMessage = '登录失败，请检查网络或账号密码';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSubmitError(errorMessage);
      setCaptchaError(true);
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    clearErrors();

    if (!validateRegister()) return;

    setLoading(true);

    try {
      const registerResponse = await request.post('/api/AppSystemDB/register', {
        username: registerForm.username.trim(),
        email: registerForm.email.trim(),
        password: registerForm.password,
        inviteCode: registerForm.inviteCode.trim(),
        captchaId,
        captchaCode: userInputCode.trim().toUpperCase()
      });

      if (!registerResponse.data?.success) {
        throw new Error(registerResponse.data?.message || '注册失败');
      }

      setMode('login');
      setLoginForm({
        email: registerForm.email.trim(),
        password: ''
      });

      setRegisterForm({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        inviteCode: ''
      });

      setSubmitError('注册成功，请登录');
      setErrors({});
      refreshCaptcha();
    } catch (error) {
      console.error('注册错误:', error);

      let errorMessage = '注册失败，请稍后重试';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (errorMessage.includes('用户名已存在')) {
        setErrors({ username: '用户名已存在，请选择其他用户名' });
      } else if (errorMessage.includes('邮箱已被注册')) {
        setErrors({ email: '该邮箱已被注册，请更换邮箱' });
      } else if (errorMessage.includes('邀请码')) {
        setErrors({ inviteCode: errorMessage });
      } else if (errorMessage.includes('验证码')) {
        setErrors({ verificationCode: errorMessage });
        setCaptchaError(true);
      } else {
        setErrors({ submit: errorMessage });
        setSubmitError(errorMessage);
      }

      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = (nextMode) => {
    setMode(nextMode);
    clearErrors();
    setUserInputCode('');
    setShowPassword(false);
    fetchCaptcha();
  };

  return (
    <div
      className={styles.body}
      // style={{ backgroundImage: `url(${backgroundImg})` }}
    >
      <div className={styles.overlay}></div>

      <div className={styles.splitContainer}>
        <div className={styles.contentWrap}>
          <AuthCharacters
            currentEmail={currentEmail}
            currentPassword={currentPassword}
            showPassword={showPassword}
          />

          <div className={styles.formArea}>
            <div className={styles.loginBox}>
              <div className={styles.header}>
                <img src={logoImg} alt="Logo" className={styles.logo} />
                <h1 className={styles.companyName}>{companyName}</h1>
              </div>

              <h2 className={styles.prompt}>
                {mode === 'login' ? '请登录' : '创建新账户'}
              </h2>

              {mode === 'login' ? (
                <form onSubmit={handleLoginSubmit}>
                  <div className={styles.userBox}>
                    <input
                      type="email"
                      required
                      value={loginForm.email}
                      onChange={(e) => {
                        setLoginForm(prev => ({ ...prev, email: e.target.value }));
                        if (submitError) setSubmitError('');
                        if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
                      }}
                      disabled={loading}
                      data-filled={loginForm.email ? 'true' : 'false'}
                    />
                    <label className={styles.floatingLabel}>邮箱地址</label>
                    {errors.email && <div className={styles.fieldError}>{errors.email}</div>}
                  </div>

                  <div className={styles.userBox}>
                    <div className={styles.passwordWrapper}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={loginForm.password}
                        onChange={(e) => {
                          setLoginForm(prev => ({ ...prev, password: e.target.value }));
                          if (submitError) setSubmitError('');
                          if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                        }}
                        disabled={loading}
                        data-filled={loginForm.password ? 'true' : 'false'}
                      />
                      <button
                        type="button"
                        className={styles.togglePassword}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                      >
                        {showPassword ? '隐藏' : '显示'}
                      </button>
                    </div>
                    <label className={styles.floatingLabel}>密码</label>
                    {errors.password && <div className={styles.fieldError}>{errors.password}</div>}
                  </div>

                  <div className={styles.captchaBox}>
                    <div
                      className={styles.captchaSvgBox}
                      title="点击刷新"
                      onClick={refreshCaptcha}
                    >
                      {captchaLoading ? (
                        <span>加载中...</span>
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: captchaSvg }} />
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.refreshBtn}
                      onClick={refreshCaptcha}
                      disabled={loading || captchaLoading}
                    >
                      ↻ 刷新
                    </button>
                  </div>

                  <div className={styles.userBox}>
                    <input
                      type="text"
                      required
                      value={userInputCode}
                      onChange={(e) => {
                        setUserInputCode(e.target.value.toUpperCase());
                        setCaptchaError(false);
                        if (submitError) setSubmitError('');
                        if (errors.verificationCode) {
                          setErrors(prev => ({ ...prev, verificationCode: '' }));
                        }
                      }}
                      className={`${captchaError ? styles.errorInput : ''}`}
                      placeholder="请输入验证码"
                      maxLength="4"
                      disabled={loading}
                      data-filled={userInputCode ? 'true' : 'false'}
                    />
                    {errors.verificationCode && (
                      <div className={styles.fieldError}>{errors.verificationCode}</div>
                    )}
                  </div>

                  {submitError && <div className={styles.submitError}>{submitError}</div>}

                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={loading || captchaLoading}
                  >
                    {loading ? '登录中...' : '登 录'}
                  </button>

                  <div className={styles.switchModeRow}>
                    <span>还没有账号？</span>
                    <button
                      type="button"
                      className={styles.switchModeBtn}
                      onClick={() => toggleMode('register')}
                      disabled={loading}
                    >
                      立即注册
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleRegisterSubmit}>
                  <div className={styles.userBox}>
                    <input
                      type="text"
                      required
                      value={registerForm.username}
                      onChange={(e) => {
                        setRegisterForm(prev => ({ ...prev, username: e.target.value }));
                        if (submitError) setSubmitError('');
                        if (errors.username) setErrors(prev => ({ ...prev, username: '' }));
                      }}
                      disabled={loading}
                      data-filled={registerForm.username ? 'true' : 'false'}
                    />
                    <label className={styles.floatingLabel}>用户名</label>
                    {errors.username && <div className={styles.fieldError}>{errors.username}</div>}
                  </div>

                  <div className={styles.userBox}>
                    <input
                      type="email"
                      required
                      value={registerForm.email}
                      onChange={(e) => {
                        setRegisterForm(prev => ({ ...prev, email: e.target.value }));
                        if (submitError) setSubmitError('');
                        if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
                      }}
                      disabled={loading}
                      data-filled={registerForm.email ? 'true' : 'false'}
                    />
                    <label className={styles.floatingLabel}>邮箱地址</label>
                    {errors.email && <div className={styles.fieldError}>{errors.email}</div>}
                  </div>

                  <div className={styles.userBox}>
                    <div className={styles.passwordWrapper}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={registerForm.password}
                        onChange={(e) => {
                          setRegisterForm(prev => ({ ...prev, password: e.target.value }));
                          if (submitError) setSubmitError('');
                          if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                        }}
                        disabled={loading}
                        data-filled={registerForm.password ? 'true' : 'false'}
                      />
                      <button
                        type="button"
                        className={styles.togglePassword}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                      >
                        {showPassword ? '隐藏' : '显示'}
                      </button>
                    </div>
                    <label className={styles.floatingLabel}>密码</label>
                    {errors.password && <div className={styles.fieldError}>{errors.password}</div>}
                  </div>

                  <div className={styles.userBox}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={registerForm.confirmPassword}
                      onChange={(e) => {
                        setRegisterForm(prev => ({ ...prev, confirmPassword: e.target.value }));
                        if (submitError) setSubmitError('');
                        if (errors.confirmPassword) {
                          setErrors(prev => ({ ...prev, confirmPassword: '' }));
                        }
                      }}
                      disabled={loading}
                      data-filled={registerForm.confirmPassword ? 'true' : 'false'}
                    />
                    <label className={styles.floatingLabel}>确认密码</label>
                    {errors.confirmPassword && (
                      <div className={styles.fieldError}>{errors.confirmPassword}</div>
                    )}
                  </div>

                  <div className={styles.userBox}>
                    <input
                      type="text"
                      required
                      value={registerForm.inviteCode}
                      onChange={(e) => {
                        setRegisterForm(prev => ({ ...prev, inviteCode: e.target.value }));
                        if (submitError) setSubmitError('');
                        if (errors.inviteCode) setErrors(prev => ({ ...prev, inviteCode: '' }));
                      }}
                      disabled={loading}
                      data-filled={registerForm.inviteCode ? 'true' : 'false'}
                    />
                    <label className={styles.floatingLabel}>邀请码</label>
                    {errors.inviteCode && <div className={styles.fieldError}>{errors.inviteCode}</div>}
                  </div>

                  <div className={styles.captchaBox}>
                    <div
                      className={styles.captchaSvgBox}
                      title="点击刷新"
                      onClick={refreshCaptcha}
                    >
                      {captchaLoading ? (
                        <span>加载中...</span>
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: captchaSvg }} />
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.refreshBtn}
                      onClick={refreshCaptcha}
                      disabled={loading || captchaLoading}
                    >
                      ↻ 刷新
                    </button>
                  </div>

                  <div className={styles.userBox}>
                    <input
                      type="text"
                      required
                      value={userInputCode}
                      onChange={(e) => {
                        setUserInputCode(e.target.value.toUpperCase());
                        setCaptchaError(false);
                        if (submitError) setSubmitError('');
                        if (errors.verificationCode) {
                          setErrors(prev => ({ ...prev, verificationCode: '' }));
                        }
                      }}
                      className={`${captchaError ? styles.errorInput : ''}`}
                      placeholder="请输入验证码"
                      maxLength="4"
                      disabled={loading}
                      data-filled={userInputCode ? 'true' : 'false'}
                    />
                    {errors.verificationCode && (
                      <div className={styles.fieldError}>{errors.verificationCode}</div>
                    )}
                  </div>

                  {submitError && <div className={styles.submitError}>{submitError}</div>}

                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={loading || captchaLoading}
                  >
                    {loading ? '注册中...' : '注 册'}
                  </button>

                  <div className={styles.switchModeRow}>
                    <span>已有账号？</span>
                    <button
                      type="button"
                      className={styles.switchModeBtn}
                      onClick={() => toggleMode('login')}
                      disabled={loading}
                    >
                      立即登录
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;